import { Link } from 'react-router-dom';
import { Crown, Instagram, MessageCircle, MapPin, Phone, Mail } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';

const linkCls = "flex items-center gap-3 text-sm text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300";
const socialCls = "w-10 h-10 rounded-full border border-[rgba(255,255,255,0.1)] flex items-center justify-center hover:border-[#D4AF37] transition-colors duration-300";

const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="bg-[#0A0A0C] border-t border-[rgba(255,255,255,0.06)]" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 mb-6" dir="ltr">
              <Crown className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
              <span className="brand-logo font-['Cormorant_Garamond'] text-lg font-bold text-[#F5F2EB]">
                Princess<span className="text-[#D4AF37]">4</span>Fitness
              </span>
            </Link>
            <p className="text-[#A3A19C] text-sm leading-relaxed max-w-xs">{t('footer.about')}</p>
          </div>

          <div>
            <p className="overline mb-6">{t('footer.navigate')}</p>
            <div className="space-y-3">
              {[
                { to: '/programs', key: 'nav.programs' },
                { to: '/about', key: 'footer.about_coach' },
                { to: '/questionnaire', key: 'footer.assessment' },
                { to: '/contact', key: 'footer.contact_us' },
              ].map(link => (
                <Link key={link.to} to={link.to} className="block text-sm text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300">
                  {t(link.key)}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="overline mb-6">{t('footer.contact')}</p>
            <div className="space-y-3">
              <a href="tel:+97333216371" className={linkCls}>
                <Phone className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} /> <span className="ltr-iso">+973 3321 6371</span>
              </a>
              <a href="mailto:Princess4Fitness@gmail.com" className={linkCls}>
                <Mail className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} /> <span className="ltr-iso">Princess4Fitness@gmail.com</span>
              </a>
              <div className="flex items-center gap-3 text-sm text-[#A3A19C]">
                <MapPin className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} /> {t('common.location_value')}
              </div>
            </div>
          </div>

          <div>
            <p className="overline mb-6">{t('footer.follow')}</p>
            <div className="flex items-center gap-4 mb-8">
              <a href="https://www.instagram.com/princess4fitness" target="_blank" rel="noopener noreferrer" className={socialCls} data-testid="footer-instagram">
                <Instagram className="w-4 h-4 text-[#A3A19C]" strokeWidth={1.5} />
              </a>
              <a href="https://api.whatsapp.com/send/?phone=97333216371" target="_blank" rel="noopener noreferrer" className={socialCls} data-testid="footer-whatsapp">
                <MessageCircle className="w-4 h-4 text-[#A3A19C]" strokeWidth={1.5} />
              </a>
            </div>
            <p className="overline mb-3">{t('footer.certified')}</p>
            <div className="space-y-1.5">
              {['cert.ereps', 'cert.ifa', 'cert.saudi'].map(k => (
                <p key={k} className="text-xs text-[#A3A19C] tracking-widest uppercase">{t(k)}</p>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[rgba(255,255,255,0.06)] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#A3A19C]">&copy; {new Date().getFullYear()} Princess4Fitness. {t('footer.rights')}</p>
          <p className="text-xs text-[#8C6C73]">{t('brand.tagline')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
